if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.isBundle = this.dataset.isBundle === 'true';
        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.configuratorForm = document.getElementById('computer-configurator-form');

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions || this.configuratorForm);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.configuratorPriceListener) {
          document.removeEventListener('configurator:priceUpdate', this.configuratorPriceListener);
        }
      }

      init() {
        // For bundle products, use configurator form
        if (this.isBundle && this.configuratorForm) {
          this.initBundleProduct();
          return;
        }

        // For regular products, use main product form
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      initBundleProduct() {
        // Find the configurator summary section to observe
        const configuratorSummary = this.configuratorForm.querySelector('.configurator-summary');

        if (configuratorSummary) {
          const rootMargin = `${configuratorSummary.offsetHeight}px 0px 0px 0px`;
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.classList.toggle(this.classes.isOpen, isAboveViewport);
              });
            },
            { threshold: 1, rootMargin }
          );
          this.observer.observe(configuratorSummary);
        }

        // Listen for configurator price updates
        this.configuratorPriceListener = (event) => {
          this.updatePriceFromConfigurator(event.detail);
        };
        document.addEventListener('configurator:priceUpdate', this.configuratorPriceListener);
      }

      onClick() {
        if (this.isBundle && this.configuratorForm) {
          // For bundle products, trigger the configurator form submission
          this.submitEl.setAttribute('aria-disabled', 'true');
          this.submitEl.classList.add('btn--loading');

          // Trigger the configurator form's submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          this.configuratorForm.dispatchEvent(submitEvent);
          return;
        }

        // Regular product behavior
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        if (!target) return;

        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePriceFromConfigurator(priceData) {
        const priceContainer = this.querySelector('.sticky-bar-price-container');
        if (!priceContainer) return;

        const { pixPrice, finalPrice, compareAtPrice, bundleDiscount, pixDiscount, installmentCount, installmentValue, showInstallments } = priceData;

        // Helper function to format price
        const formatPrice = (priceInCents) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(priceInCents / 100);
        };

        // Build compact price HTML for sticky bar (matching configurator display)
        let html = '<div class="f-price f-price--with-payment-options">';

        // Show compare at price if bundle discount exists (strikethrough)
        if (bundleDiscount > 0 && compareAtPrice > finalPrice) {
          html += `
            <div class="f-price__compare">
              <s class="f-price-item f-price-item--compare">${formatPrice(compareAtPrice)}</s>
            </div>
          `;
        }

        // Main Pix price
        html += `
          <div class="f-price__main">
            <span class="f-price-item f-price-item--pix">${formatPrice(pixPrice)}</span>
          </div>
          <div class="f-price__pix-info text-subtext">
            à vista no pix com ${pixDiscount}% de desconto
          </div>
        `;

        // Show installments if applicable
        if (showInstallments) {
          html += `
            <div class="f-price__installments text-subtext">
              ou ${formatPrice(finalPrice)} em ${installmentCount}x de ${formatPrice(installmentValue)} sem juros no cartão
            </div>
          `;
        }

        html += '</div>';

        priceContainer.innerHTML = html;
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper && unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        if (!this.mainProductInfo) return;

        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
